import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import { cn } from './utils';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    asChild?: boolean;
    variant?: 'primary' | 'ghost' | 'danger';
    /** Exibe spinner e desabilita o botão enquanto a ação está em andamento. */
    loading?: boolean;
    /** Texto exibido durante loading (padrão: mantém children). */
    loadingText?: ReactNode;
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            asChild = false,
            variant = 'primary',
            type,
            loading = false,
            loadingText,
            disabled,
            children,
            ...props
        },
        ref,
    ) => {
        const isDisabled = disabled || loading;
        const label = loading && loadingText !== undefined ? loadingText : children;

        if (asChild) {
            return (
                <Slot
                    ref={ref}
                    aria-disabled={isDisabled || undefined}
                    aria-busy={loading || undefined}
                    className={cn(
                        'nexus-btn',
                        variant === 'primary' && 'nexus-btn--primary',
                        variant === 'ghost' && 'nexus-btn--ghost',
                        variant === 'danger' && 'nexus-btn--danger',
                        isDisabled && 'nexus-btn--disabled',
                        className,
                    )}
                    {...props}
                >
                    {children}
                </Slot>
            );
        }

        return (
            <button
                ref={ref}
                type={type ?? 'button'}
                disabled={isDisabled}
                aria-busy={loading || undefined}
                className={cn(
                    'nexus-btn',
                    variant === 'primary' && 'nexus-btn--primary',
                    variant === 'ghost' && 'nexus-btn--ghost',
                    variant === 'danger' && 'nexus-btn--danger',
                    loading && 'nexus-btn--loading',
                    className,
                )}
                {...props}
            >
                {loading ? (
                    <Loader2
                        size={14}
                        strokeWidth={2.2}
                        className="nexus-btn__spinner nx-spin"
                        aria-hidden
                    />
                ) : null}
                {label ? <span className="nexus-btn__label">{label}</span> : null}
            </button>
        );
    },
);

Button.displayName = 'Button';

export { Button };
